/**-- external -- */
import React, { useContext, useMemo } from "react";
import { withRouter } from "react-router";

/**-- internal -- */
import { getDisplayName } from "Utils/HOCHelpers";

/**-- relative -- */
import { ControlParameterContext } from "../modules/ControlParameterContext";

const DEFAULT_ROUTE_PARAMS = {};

/**
 * Purpose: This HOC will maintain relevant contexts based on the route and pass it
 * to the component for control parameter fetching.
 */
const withRouteBasedControlParametersContext = Component => {
  function ControlParameterContextHOC(props) {
    /** Here, routeParams are always based on the current route(Ex: :curriculumId,:courseId etc)
     *  They are not affected by whether the current pathname has further child paths or not.
     *  (Effectively, for path like /:platform and /:platform/:courses, routeParams will be same)
     */
    const { routeParams = DEFAULT_ROUTE_PARAMS } = props;

    const parentControlParameterContext = useContext(ControlParameterContext);

    /**
     * Memoization is required to avoid extra rendering. This memoization is broken when route is updated.
     * (Due to routeParams reference change in react-router)
     */
    const finalContext = useMemo(() => {
      return { ...routeParams, ...parentControlParameterContext };
    }, [routeParams, parentControlParameterContext]);

    return (
      <ControlParameterContext.Provider value={finalContext}>
        <Component {...props} controlParameterContext={finalContext} />
      </ControlParameterContext.Provider>
    );
  }

  const Wrapper = withRouter(ControlParameterContextHOC);

  Wrapper.displayName = `withRouteBasedControlParametersContext(${getDisplayName(
    Component
  )})`;

  return Wrapper;
};

export default withRouteBasedControlParametersContext;
